"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { ApiError, errorMessage, post } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import type { Agreements, LoginResponse } from "@/lib/types";
import { AgreementList, EMPTY_AGREEMENTS, requiredAgreed } from "@/components/AgreementList";
import { AuthShell } from "@/components/AuthShell";
import { Guard } from "@/components/Guard";
import { KakaoButton } from "@/components/KakaoButton";
import { Button } from "@/components/ui/Button";
import { TextField } from "@/components/ui/Field";
import { Alert, Divider } from "@/components/ui/Surface";

type Step = "email" | "code" | "account";

export default function SignupPage() {
  return (
    <Guard need="anonymous">
      <SignupFlow />
    </Guard>
  );
}

function SignupFlow() {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [resendAt, setResendAt] = useState(0);

  const footer = (
    <>
      이미 계정이 있으신가요?{" "}
      <Link href="/login" className="font-semibold text-primary-900">
        로그인
      </Link>
    </>
  );

  if (step === "email") {
    return (
      <AuthShell title="회원가입" description="이메일 인증 후 바로 시작할 수 있습니다." footer={footer} step={{ current: 1, total: 3, label: "이메일 입력" }}>
        <EmailStep
          initial={email}
          onSent={(addr, resendAfter) => {
            setEmail(addr);
            setResendAt(Date.now() + resendAfter * 1000);
            setStep("code");
          }}
        />
      </AuthShell>
    );
  }
  if (step === "code") {
    return (
      <AuthShell
        title="인증번호 입력"
        description={
          <>
            <span className="font-semibold text-gray-900">{email}</span> 로 보낸 6자리 번호를 입력해 주세요.
          </>
        }
        footer={footer}
        step={{ current: 2, total: 3, label: "이메일 인증" }}
      >
        <CodeStep
          email={email}
          resendAt={resendAt}
          onResent={(s) => setResendAt(Date.now() + s * 1000)}
          onBack={() => setStep("email")}
          onVerified={(token) => {
            setVerificationToken(token);
            setStep("account");
          }}
        />
      </AuthShell>
    );
  }
  return (
    <AuthShell title="계정 정보" description="로그인에 쓸 비밀번호와 이름을 정해 주세요." step={{ current: 3, total: 3, label: "계정 만들기" }}>
      <AccountStep email={email} verificationToken={verificationToken} onRestart={() => setStep("email")} />
    </AuthShell>
  );
}

function EmailStep({ initial, onSent }: { initial: string; onSent: (email: string, resendAfter: number) => void }) {
  const [email, setEmail] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("이메일 형식이 올바르지 않습니다.");
      return;
    }
    setLoading(true);
    try {
      const r = await post<{ resendAfterSeconds: number }>("/v1/auth/email/send-code", { email: email.trim() });
      onSent(email.trim(), r.resendAfterSeconds);
    } catch (err) {
      if (err instanceof ApiError && err.code === "VERIFICATION_COOLDOWN") {
        onSent(email.trim(), Number(err.props.retryAfterSeconds ?? 60));
        return;
      }
      setError(err instanceof ApiError && err.code === "VALIDATION_FAILED" ? (err.fieldErrors.email ?? err.message) : errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <KakaoButton label="카카오로 가입하기" />
      <Divider label="또는 이메일로 가입" />
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <TextField
          label="이메일"
          type="email"
          autoComplete="email"
          placeholder="name@company.com"
          hint="입찰 공고 알림과 계정 안내를 받을 주소입니다."
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={error}
          autoFocus
        />
        <Button type="submit" size="lg" block loading={loading} disabled={!email.trim()}>
          인증번호 받기
        </Button>
      </form>
    </div>
  );
}

function useCountdown(until: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(t);
  }, []);
  return Math.max(0, Math.ceil((until - now) / 1000));
}

function CodeStep({
  email,
  resendAt,
  onResent,
  onBack,
  onVerified,
}: {
  email: string;
  resendAt: number;
  onResent: (seconds: number) => void;
  onBack: () => void;
  onVerified: (token: string) => void;
}) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const remain = useCountdown(resendAt);
  const inputRef = useRef<HTMLInputElement>(null);

  const verify = async (value: string) => {
    setError(null);
    setNotice(null);
    setLoading(true);
    try {
      const r = await post<{ verificationToken: string }>("/v1/auth/email/verify-code", { email, code: value });
      onVerified(r.verificationToken);
    } catch (err) {
      if (err instanceof ApiError && err.code === "VERIFICATION_CODE_MISMATCH") {
        setError(`인증번호가 일치하지 않습니다. 남은 입력 횟수 ${err.props.remainingAttempts}회`);
      } else {
        setError(errorMessage(err));
      }
      setCode("");
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setError(null);
    setResending(true);
    try {
      const r = await post<{ resendAfterSeconds: number }>("/v1/auth/email/send-code", { email });
      onResent(r.resendAfterSeconds);
      setNotice("인증번호를 다시 보냈습니다. 가장 최근 메일의 번호를 입력해 주세요.");
      setCode("");
    } catch (err) {
      if (err instanceof ApiError && err.code === "VERIFICATION_COOLDOWN") onResent(Number(err.props.retryAfterSeconds ?? 60));
      setError(errorMessage(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (code.length === 6) void verify(code);
      }}
    >
      <TextField
        ref={inputRef}
        label="인증번호"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="6자리 숫자"
        maxLength={6}
        value={code}
        className="[&_input]:text-center [&_input]:text-xl [&_input]:tracking-[0.5em]"
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, "").slice(0, 6);
          setCode(v);
          if (v.length === 6 && !loading) void verify(v);
        }}
        error={error}
        hint="인증번호는 10분 동안 유효합니다. 메일이 보이지 않으면 스팸함을 확인해 주세요."
        autoFocus
      />
      {notice && <Alert tone="info">{notice}</Alert>}
      <Button type="submit" size="lg" block loading={loading} disabled={code.length !== 6}>
        확인
      </Button>
      <div className="flex items-center justify-between text-sm">
        <button type="button" onClick={onBack} className="text-gray-600 hover:text-gray-900">
          이메일 다시 입력
        </button>
        <button
          type="button"
          onClick={resend}
          disabled={remain > 0 || resending}
          className="font-semibold text-primary-900 disabled:font-medium disabled:text-gray-500"
        >
          {remain > 0 ? `${remain}초 후 재발송` : "인증번호 재발송"}
        </button>
      </div>
    </form>
  );
}

function AccountStep({ email, verificationToken, onRestart }: { email: string; verificationToken: string; onRestart: () => void }) {
  const { signIn } = useAuth();
  const router = useRouter();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [agreements, setAgreements] = useState<Agreements>(EMPTY_AGREEMENTS);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(false);

  const pwOk = password.length >= 8 && /[A-Za-z]/.test(password) && /\d/.test(password);
  const pwMatch = password2.length > 0 && password === password2;
  const ready = name.trim() && pwOk && pwMatch && requiredAgreed(agreements);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setError(null);
    setLoading(true);
    try {
      const res = await post<LoginResponse>("/v1/auth/signup", {
        email,
        verificationToken,
        password,
        name: name.trim(),
        agreements,
      });
      signIn(res);
      router.replace("/app");
    } catch (err) {
      if (err instanceof ApiError && err.code === "SIGNUP_TOKEN_INVALID") setExpired(true);
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <TextField label="이메일" value={email} disabled readOnly hint="인증이 완료된 이메일입니다." />
      <TextField label="이름" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={50} autoFocus />
      <TextField
        label="비밀번호"
        type="password"
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        hint="8자 이상, 영문과 숫자를 모두 포함해 주세요."
        error={password && !pwOk ? "8자 이상이며 영문과 숫자를 모두 포함해야 합니다." : null}
      />
      <TextField
        label="비밀번호 확인"
        type="password"
        autoComplete="new-password"
        value={password2}
        onChange={(e) => setPassword2(e.target.value)}
        error={password2 && !pwMatch ? "비밀번호가 일치하지 않습니다." : null}
      />
      <AgreementList value={agreements} onChange={setAgreements} />
      {error && (
        <Alert tone="negative">
          {error}
          {expired && (
            <button type="button" onClick={onRestart} className="ml-2 font-semibold underline">
              처음부터 다시
            </button>
          )}
        </Alert>
      )}
      <Button type="submit" size="lg" block loading={loading} disabled={!ready} className="mt-2">
        가입하기
      </Button>
    </form>
  );
}
